import { useEffect, useState } from 'react';
import { formatMessageTypeAvailableLift } from 'modules/intelligentInsights/frequencyOptimization/components/breakdown/breakdownUtils';
import {
  MessageTypeHeaderCell,
  RightAlignedMessageTypeHeaderCell,
} from 'modules/intelligentInsights/frequencyOptimization/components/breakdown/messageType/BreakdownMessageTypeTable.styles';
import { MessageTypeRowData } from 'modules/intelligentInsights/frequencyOptimization/types';
import {
  ARROW_DIRECTION_DOWN,
  ARROW_DIRECTION_UP,
  CollapsibleContentArrow,
} from 'ui/collapsibleContent';
import { CollapsibleContent } from 'ui/collapsibleContent';
import { BUTTON_SIZES, BUTTON_VARIANTS } from 'ui/button';
import { FlexSpacer } from 'ui/flexSpacer';
import { colors, spacing } from 'ui/theme';
import { HelpTooltip } from 'ui/tooltip';
import {
  BREAKDOWN_AUDIENCE_MESSAGE_TYPE_CAMPAIGNS_TOGGLETIP,
  BREAKDOWN_AUDIENCE_MESSAGE_TYPE_COLLAPSE_CAMPAIGNS,
  BREAKDOWN_AUDIENCE_MESSAGE_TYPE_TOP_CAMPAIGNS,
} from 'modules/intelligentInsights/constants';
import {
  ArrowButton,
  CampaignImg,
  CampaignInfoContainer,
  CampaignInfoIdDetails,
  CampaignInfoIdDetailsContent,
  CampaignRow,
  CampaignsTextFlex,
  CampaignThumbnailContainer,
  MessageTypeName,
  SecondaryBody2,
  StyledLink,
  StyledTruncatedText,
} from 'modules/intelligentInsights/frequencyOptimization/components/breakdown/messageType/BreakdownMessageTypeCells.styles';

export const BREAKDOWN_MESSAGE_TYPE_NAME_CELL = 'breakdown-message-type-name';
export const BREAKDOWN_MESSAGE_TYPE_CAMPAIGN_ROW = 'breakdown-message-campaign-row';
export const BREAKDOWN_MESSAGE_TYPE_CAMPAIGN_ACCORDIAN = 'breakdown-message-campaign-accordian';
export const BREAKDOWN_MESSAGE_TYPE_CAMPAIGNS_CONTENT = 'breakdown-message-campaigns-content';

export type MessageTypeRowCell = ({ row }: { row: MessageTypeRowData }) => React.ReactElement;

export const MessageTypeNameCell: MessageTypeRowCell = ({ row }) => {
  const [isCollapsed, toggleIsCollapsed] = useState(true);

  useEffect(() => {
    // Reset in-between pagination changes
    toggleIsCollapsed(true);
  }, [row.messageTypeId]);
  const hasCampaigns = row.campaigns.length > 0;
  const campaignRows = row.campaigns.map((campaign) => {
    return (
      <CampaignRow
        data-test={`${BREAKDOWN_MESSAGE_TYPE_CAMPAIGN_ROW}-${campaign.campaignId}`}
        key={campaign.campaignId.toString()}
      >
        <CampaignThumbnailContainer>
          <CampaignImg src={campaign.templateUrl} height={36} width={42} alt={`campaign preview`} />
        </CampaignThumbnailContainer>
        <CampaignInfoContainer>
          <StyledLink target="_blank" rel="noreferrer noopener" href={'/'}>
            <StyledTruncatedText textOnly maxWidth={430} name={campaign.campaignName}>
              {campaign.campaignName}
            </StyledTruncatedText>
          </StyledLink>
          <div>
            <CampaignInfoIdDetails>{campaign.campaignType}</CampaignInfoIdDetails>
            <CampaignInfoIdDetailsContent>ID: {campaign.campaignId}</CampaignInfoIdDetailsContent>
          </div>
        </CampaignInfoContainer>
      </CampaignRow>
    );
  });

  return (
    <>
      <MessageTypeHeaderCell data-test={BREAKDOWN_MESSAGE_TYPE_NAME_CELL}>
        <FlexSpacer flexDirection="row" alignItems="center" justifyContent="space-between">
          <MessageTypeName>{row.name}</MessageTypeName>
          {hasCampaigns && (
            <FlexSpacer
              alignItems="center"
              data-test={BREAKDOWN_MESSAGE_TYPE_CAMPAIGN_ACCORDIAN}
              flexDirection="row"
              gap="half"
            >
              <SecondaryBody2>{BREAKDOWN_AUDIENCE_MESSAGE_TYPE_TOP_CAMPAIGNS}</SecondaryBody2>
              <ArrowButton
                onClick={() => toggleIsCollapsed((prev) => !prev)}
                size={BUTTON_SIZES.SMALL}
                variant={BUTTON_VARIANTS.GHOST}
              >
                <CollapsibleContentArrow
                  isCollapsed={isCollapsed}
                  arrowStart={ARROW_DIRECTION_DOWN}
                  arrowEnd={ARROW_DIRECTION_UP}
                  width={16}
                  height={16}
                />
              </ArrowButton>
            </FlexSpacer>
          )}
        </FlexSpacer>
        {hasCampaigns && (
          <CollapsibleContent isCollapsed={isCollapsed}>
            <FlexSpacer
              alignItems="flex-start"
              data-test={BREAKDOWN_MESSAGE_TYPE_CAMPAIGNS_CONTENT}
              flexDirection="column"
              gap={spacing.threeQuarter}
            >
              <CampaignsTextFlex flexDirection="row" gap="quarter" alignItems="center">
                <SecondaryBody2>
                  {BREAKDOWN_AUDIENCE_MESSAGE_TYPE_COLLAPSE_CAMPAIGNS}
                </SecondaryBody2>
                <HelpTooltip
                  iconProps={{ color: colors.iconPrimary, width: '16px', height: '16px' }}
                  tooltipContent={BREAKDOWN_AUDIENCE_MESSAGE_TYPE_CAMPAIGNS_TOGGLETIP}
                />
              </CampaignsTextFlex>
              {campaignRows}
            </FlexSpacer>
          </CollapsibleContent>
        )}
      </MessageTypeHeaderCell>
    </>
  );
};

export const MessageTypeOpenRateCell: MessageTypeRowCell = ({ row }) => (
  <RightAlignedMessageTypeHeaderCell>
    {formatMessageTypeAvailableLift(row.openRateLift)}
  </RightAlignedMessageTypeHeaderCell>
);

export const MessageTypeClickRateCell: MessageTypeRowCell = ({ row }) => (
  <RightAlignedMessageTypeHeaderCell>
    {formatMessageTypeAvailableLift(row.clickRateLift)}
  </RightAlignedMessageTypeHeaderCell>
);

export const MessageTypeUnsubRateCell: MessageTypeRowCell = ({ row }) => (
  <RightAlignedMessageTypeHeaderCell>
    {formatMessageTypeAvailableLift(row.unsubscribeRateLift)}
  </RightAlignedMessageTypeHeaderCell>
);

export const MessageTypeSkippedCell: MessageTypeRowCell = ({ row }) => (
  <RightAlignedMessageTypeHeaderCell>
    {formatMessageTypeAvailableLift(row.messagesSkipped)}
  </RightAlignedMessageTypeHeaderCell>
);
